import {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {Avatar, Box, Chip, FormHelperText, Grid, IconButton, TextField, Tooltip, Typography} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {goldErrorFieldSx, useStyles} from "../../utils/uiUtils";
import {addressValidationRules, cityValidationRules, countryValidationRules, postalCodeValidationRules, stateValidationRules} from "../../utils/validationRules";
import {getLocationDetails} from "../../bindCountryState";

/**
 * AddressBadge — renders an address as either a compact Chip badge or expanded fields.
 *
 * Props:
 *   value          — { address_line_1?, address_line_2?, city?, state?, country?, postal_code? }
 *                    Pass null/undefined for an empty address.
 *   onChange       — (addressObject) => void — called on confirmed save (Enter) only.
 *                    Omit for display-only usage.
 *   onSave         — () => void — called after onChange on Enter. Omit to just collapse.
 *   label          — optional heading above the badge/fields (string)
 *   fields         — which fields to show in expanded mode. Defaults to all 6 standard fields.
 *   canToggle      — if true, shows the expand/collapse toggle button (default false)
 *   active         — if false, fields are rendered as disabled/read-only (default true)
 *   requiredFields — fields that must be filled when editing.
 *                    Defaults to ['address_line_1', 'state', 'country', 'postal_code'].
 *                    Pass [] to disable all required validation.
 */

const ALL_FIELDS = ["address_line_1", "address_line_2", "postal_code", "city", "state", "country"];

const FIELD_CONFIG = {
    address_line_1: {label: "Address Line 1", xs: 12, rules: addressValidationRules,  required: true},
    address_line_2: {label: "Address Line 2", xs: 12, rules: null},
    postal_code:    {label: "Postcode",        xs: 4,  rules: postalCodeValidationRules, required: true},
    city:           {label: "City",            xs: 4,  rules: cityValidationRules},
    state:          {label: "State",           xs: 4,  rules: stateValidationRules,    required: true},
    country:        {label: "Country",         xs: 8,  rules: countryValidationRules,  required: true},
};

const DEFAULT_REQUIRED_FIELDS = Object.entries(FIELD_CONFIG)
    .filter(([, cfg]) => cfg.required)
    .map(([field]) => field);  // ['address_line_1', 'postal_code', 'state', 'country']

const validateField = (field, value, isRequired) => {
    if (isRequired && !value?.trim()) return `${FIELD_CONFIG[field].label} is required`;
    const rules = FIELD_CONFIG[field]?.rules;
    if (!rules || !value) return null;
    if (rules.pattern && !rules.pattern.value.test(value)) return rules.pattern.message;
    return null;
};

const truncate = (str, max) => str?.length > max ? str.slice(0, max) + '…' : str;

const formatBadge = (addr, fields) => {
    if (!addr) return "—";
    const parts = [];

    const line1 = fields.includes("address_line_1") && addr.address_line_1;
    const line2 = fields.includes("address_line_2") && addr.address_line_2;

    if (line1 && line2) {
        const combined = `${addr.address_line_1}, ${addr.address_line_2}`;
        if (combined.length <= 28) {
            parts.push(combined);
        } else {
            parts.push(truncate(addr.address_line_1, 24));
        }
    } else if (line1) {
        parts.push(truncate(addr.address_line_1, 28));
    }
    if (fields.includes("city") && addr.city) parts.push(addr.city);
    const statePostal = [
        fields.includes("state") && addr.state,
        fields.includes("postal_code") && addr.postal_code,
    ].filter(Boolean).join(" ");
    if (statePostal) parts.push(statePostal);
    if (fields.includes("country") && addr.country) parts.push(addr.country);
    return parts.length ? parts.join(", ") : "—";
};

const TOOLTIP_LABELS = {
    address_line_1: "Address",
    address_line_2: "Address 2",
    postal_code:    "Postcode",
    city:           "City",
    state:          "State",
    country:        "Country",
};

const TooltipContent = ({addr, fields}) => (
    <Box>
        {ALL_FIELDS.filter(f => fields.includes(f) && addr?.[f]).map(f => (
            <Typography key={f} variant="body2">
                <span style={{opacity: 0.7}}>{TOOLTIP_LABELS[f]}:</span> {addr[f]}
            </Typography>
        ))}
    </Box>
);

const LocationAvatar = ({size = "md"}) => {
    const avatarSize = size === "sm" ? 28 : 40;
    return (
        <Avatar sx={{
            width: avatarSize,
            height: avatarSize,
            bgcolor: "rgba(255,197,64,0.15)",
            border: "1px solid rgba(255,197,64,0.4)",
        }}>
            <LocationOnOutlinedIcon sx={{fontSize: avatarSize * 0.55, color: "#FFC540"}}/>
        </Avatar>
    );
};

export const AddressBadge = forwardRef(({
    value,
    onChange,
    onSave,
    onDelete,
    label,
    fields = ALL_FIELDS,
    canToggle = false,
    active = true,
    actions,
    size = "md",
    requiredFields = DEFAULT_REQUIRED_FIELDS,
}, ref) => {
    const classes = useStyles();
    const empty = !value || !fields.some(f => value[f]);
    const [expanded, setExpanded] = useState(empty);
    const [localValues, setLocalValues] = useState(value || {});
    const [localErrors, setLocalErrors] = useState({});
    const [touched, setTouched] = useState(new Set());
    const [geoFilling, setGeoFilling] = useState(false);
    const visibleFields = ALL_FIELDS.filter(f => fields.includes(f));

    // Expose validate() and flush() so parent components can imperatively trigger
    // validation or push local edits upstream without the operator pressing Enter.
    useImperativeHandle(ref, () => ({
        validate: () => {
            const allTouched = new Set(visibleFields);
            setTouched(allTouched);
            const errors = {};
            visibleFields.forEach(f => {
                errors[f] = validateField(f, localValues[f], requiredFields.includes(f));
            });
            setLocalErrors(errors);
            const isValid = !Object.values(errors).some(Boolean);
            if (!isValid) setExpanded(true);
            return isValid;
        },
        // Push local edits upstream via onChange without collapsing the badge.
        // Call before an external submit button so the parent form receives the
        // current local values even if the operator never pressed Enter inside the badge.
        flush: () => {
            if (touched.size > 0 && onChange) onChange(localValues);
        },
    }));

    // Sync from external value only (dialog pick, parent form update).
    // Internal edits do NOT call onChange — value prop never bounces back —
    // so this effect only fires on genuine external updates.
    useEffect(() => {
        if (!value) return;
        setLocalValues(value);
        setLocalErrors({});
        setTouched(new Set());
    }, [JSON.stringify(value)]);

    const handleCollapse = () => {
        setExpanded(false);
        setLocalValues(value || {});
        setLocalErrors({});
        setTouched(new Set());
    };

    const handleFieldChange = (field, fieldValue) => {
        setTouched(prev => new Set(prev).add(field));
        setLocalValues(prev => ({...prev, [field]: fieldValue}));
    };

    const handleFieldBlur = (field, fieldValue) => {
        if (touched.size === 0) return;
        const errors = {...localErrors};

        // Validate all required visible fields on every blur once editing has started
        visibleFields.forEach(f => {
            if (requiredFields.includes(f)) {
                const val = f === field ? fieldValue : localValues[f];
                errors[f] = validateField(f, val, true);
            }
        });
        // Also validate format of the blurred field if non-required
        if (!requiredFields.includes(field)) {
            errors[field] = validateField(field, fieldValue, false);
        }
        setLocalErrors(errors);

        // Geo-fill: fires when address_line_1, postal_code, or country is blurred,
        // provided the blurred field has no error, the operator has touched it,
        // and the minimum data condition is met:
        //   (address_line_1 AND postal_code) OR (address_line_1 AND country)
        const geoTriggerFields = ['address_line_1', 'postal_code', 'country'];
        const currentAddress1  = field === 'address_line_1' ? fieldValue : localValues['address_line_1'];
        const currentPostal    = field === 'postal_code'    ? fieldValue : localValues['postal_code'];
        const currentCountry   = field === 'country'        ? fieldValue : localValues['country'];
        const hasAddress1AndPostal  = currentAddress1?.trim() && currentPostal?.trim();
        const hasAddress1AndCountry = currentAddress1?.trim() && currentCountry?.trim();

        if (
            geoTriggerFields.includes(field) &&
            touched.has(field) &&
            !errors[field] &&
            (hasAddress1AndPostal || hasAddress1AndCountry)
        ) {
            const composedAddress = [currentAddress1, currentPostal, currentCountry]
                .filter(v => v?.trim())
                .join(', ');

            setGeoFilling(true);
            getLocationDetails({target: {value: composedAddress}}, (localities) => {
                if (localities?.length) {
                    const geo = localities[0];
                    const geoFields = ['city', 'state', 'country'];

                    setLocalValues(prev => {
                        // Only overwrite fields the operator has NOT manually touched
                        const updated = {
                            ...prev,
                            city:    (!touched.has('city')    && geo.city?.trim())    ? geo.city.trim()    : prev.city,
                            state:   (!touched.has('state')   && geo.state?.trim())   ? geo.state.trim()   : prev.state,
                            country: (!touched.has('country') && geo.country?.trim()) ? geo.country.trim() : prev.country,
                        };

                        // Validate geo-filled fields with their new values
                        const geoErrors = {};
                        geoFields.forEach(f => {
                            if (visibleFields.includes(f)) {
                                geoErrors[f] = validateField(f, updated[f], requiredFields.includes(f));
                            }
                        });
                        setLocalErrors(prev => ({...prev, ...geoErrors}));

                        // Mark geo-filled fields as touched so future edits validate correctly
                        setTouched(prev => {
                            const next = new Set(prev);
                            geoFields.forEach(f => next.add(f));
                            return next;
                        });

                        return updated;
                    });
                }
                setGeoFilling(false);
            });
        }
    };

    // Validate all touched fields synchronously from current localValues.
    // Used by Enter handler where localErrors state may be stale.
    const computeCurrentErrors = (currentValues, currentTouched) => {
        const errors = {...localErrors};
        currentTouched.forEach(f => {
            errors[f] = validateField(f, currentValues[f], requiredFields.includes(f));
        });
        return errors;
    };

    return (
        <Box>
            {!expanded ? (
                /* ── Collapsed: label + chip + actions on same row ── */
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    {label && (
                        <Typography sx={{...classes.labelMuted, minWidth: '140px', textAlign: 'left', color: '#BABABA !important'}}>{label}</Typography>
                    )}
                    <Tooltip
                        title={!empty ? <TooltipContent addr={value} fields={fields}/> : ""}
                        placement="top"
                        arrow
                    >
                        <Chip
                            avatar={<LocationAvatar size={size}/>}
                            label={
                                <Typography sx={{
                                    ...classes.textWhite,
                                    fontSize: size === "sm" ? 12 : 14,
                                    opacity: empty ? 0.4 : 1,
                                }}>
                                    {formatBadge(value, fields)}
                                </Typography>
                            }
                            deleteIcon={
                                canToggle
                                    ? <UnfoldMoreOutlinedIcon sx={{fontSize: 16, color: "#FFFFFF !important", "&:hover": {color: "#FFC540 !important"}}}/>
                                    : onDelete
                                        ? <DeleteOutlineIcon sx={{fontSize: 16, color: "#BABABA !important", "&:hover": {color: "#FFC540 !important"}}}/>
                                        : undefined
                            }
                            onDelete={
                                canToggle ? () => setExpanded(true) :
                                onDelete ? (e) => { e.stopPropagation(); onDelete(); } :
                                undefined
                            }
                            variant="outlined"
                            sx={{
                                pointerEvents: 'auto',
                                height: size === "sm" ? 36 : 48,
                                borderRadius: size === "sm" ? "18px" : "24px",
                                borderColor: "rgba(255,255,255,0.2)",
                                backgroundColor: "rgba(255,255,255,0.04)",
                                paddingLeft: "4px",
                                "& .MuiChip-avatar": {marginLeft: "0 !important", marginRight: "0 !important"},
                                "& .MuiChip-label": {px: 1.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
                                "& .MuiChip-label *": {color: "#FFFFFF !important", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
                                "&:hover": {
                                    borderColor: empty ? "rgba(255,255,255,0.2)" : "rgba(255,197,64,0.4)",
                                },
                            }}
                        />
                    </Tooltip>
                    {actions}
                </Box>
            ) : (
                /* ── Expanded: label + collapse button on same row, fields below ── */
                <Box>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            {label && (
                                <Typography sx={{...classes.labelMuted, textAlign: 'left', color: '#BABABA !important'}}>{label}</Typography>
                            )}
                            {actions}
                        </Box>
                        <IconButton size="small" onClick={handleCollapse} sx={{pointerEvents: 'auto', p: 0.25}}>
                            <UnfoldLessOutlinedIcon sx={{fontSize: 16, color: "#FFC540"}}/>
                            <Typography sx={{color: "#FFC540", fontFamily: "Poppins", fontSize: 12, ml: 0.5}}>Collapse</Typography>
                        </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                        {visibleFields.map((field) => {
                            const cfg = FIELD_CONFIG[field];
                            const isRequired = requiredFields.includes(field);
                            const fieldLabel = `${cfg.label}${isRequired && active ? ' *' : ''}`;
                            const hasError = Boolean(localErrors[field]);
                            const isLast = field === visibleFields[visibleFields.length - 1];
                            const xs = (field === "country" && isLast) ? 12 : cfg.xs;
                            // city and state are output-only geo-fill targets — disable during lookup
                            const isGeoField = ['city', 'state'].includes(field);
                            return (
                                <Grid item xs={xs} key={field}>
                                    <TextField
                                        label={fieldLabel}
                                        size="small"
                                        fullWidth
                                        value={localValues[field] || ""}
                                        onChange={(e) => active && handleFieldChange(field, e.target.value)}
                                        onBlur={(e) => active && handleFieldBlur(field, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const currentErrors = computeCurrentErrors(localValues, touched);
                                                setLocalErrors(currentErrors);
                                                if (Object.values(currentErrors).some(Boolean)) return;
                                                if (onChange) onChange(localValues);
                                                if (onSave) onSave();
                                                handleCollapse();
                                            }
                                        }}
                                        className={classes.selectLabelGreyDiff}
                                        sx={hasError ? goldErrorFieldSx : {}}
                                        InputLabelProps={{shrink: true}}
                                        disabled={!active || (isGeoField && geoFilling)}
                                        error={false}
                                    />
                                    {hasError && (
                                        <FormHelperText sx={{mx: 0, color: '#FFC540'}}>{localErrors[field]}</FormHelperText>
                                    )}
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}
        </Box>
    );
});
